import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "components/ui/Select";
import { SelectCustomers } from "components/ui/selectors";
import { useAllSchemeMasters, useGenerateSchemeEligibilitySummary } from "http-hooks/schemeMaster/useSchemeMaster";
import useAuth from "hooks/useAuth";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

export default function SchemeEligibilitySummary() {
  const navigate = useNavigate();
  const { agttp } = useAuth();

  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const { mutateAsync: generateReport, isPending: isGenerating } =
    useGenerateSchemeEligibilitySummary();

  const { isLoading: isSchemesLoading, data: { data: schemes } = {} } =
    useAllSchemeMasters({ ag_status: 1, noPagination: true });

  const schemeOptions = schemes?.map((scheme) => ({
    value: scheme.schemeid,
    label: `${scheme.sch_name} | Date: ${new Date(scheme.sch_date).toLocaleDateString()} | ${
      scheme.vendorDetail?.name || scheme.vendor
    }`,
  })) || [];

  const handleGenerateReport = async () => {
    try {
      if (!selectedScheme) {
        toast.error("Please select a scheme");
        return;
      }

      const payload = { schemeid: selectedScheme.value };

      if (selectedCustomers && selectedCustomers.length > 0) {
        payload.cust = selectedCustomers
          .map((c) => c.ps_Id || c.value)
          .filter((v) => v != null && v !== "");
      }

      const data = await generateReport(payload);
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);

      navigate(
        getDocumentViewerRoute(selectedScheme.value, {
          htmlUrl,
          pdfUrl,
          documentNumber: selectedScheme.label.split(" | ")[0],
          documentType: "Scheme Eligibility Summary",
        })
      );
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold leading-6 text-gray-900 text-center">
        Scheme Eligibility Summary
      </h1>

      <div className="max-w-md mx-auto mt-6 space-y-4">
        <Select
          label="Scheme *"
          value={selectedScheme}
          onChange={setSelectedScheme}
          options={schemeOptions}
          placeholder="Select scheme"
          isClearable={true}
          isSearchable={true}
          isLoading={isSchemesLoading}
        />

        {agttp === 0 && (
          <div>
            <SelectCustomers
              label="Customers (Leave empty for all)"
              value={selectedCustomers}
              onChange={setSelectedCustomers}
              isMulti={true}
              isClearable={true}
            />
            <p className="mt-1 text-xs text-gray-500">
              Select specific customers or leave empty to include all
            </p>
          </div>
        )}

        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleGenerateReport}
            disabled={isGenerating || !selectedScheme}
            className="rounded-md bg-indigo-600 px-8 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>
    </div>
  );
}
